import { Megaphone, MessagesSquare, Pin } from 'lucide-react';
import type { Announcement } from '../api/announcements';
import { formatTimestamp } from '../lib/datetime';
import { AnnouncementAttachments } from './AnnouncementAttachments';
import { CuratedText } from './CuratedText';
import { UnreadDot } from './UnreadDot';

/**
 * An announcement is **state**. A message is a **stream item**. They must never
 * look alike (spec §6) — hence: full container width never inset, a 5px ember
 * left rule, squared left corners, raised.
 *
 * Read state removes the unread dot only. The rule stays: it is what marks this
 * as standing information rather than something that scrolled past.
 *
 * Takes the API's `Announcement` directly rather than a view model of its own. There used to be a
 * hand-written shape here with a pre-formatted `date` string, which was what a fixture needed; a
 * second definition of what an announcement is would now be a place for the two to drift.
 */
export function AnnouncementCard({
  announcement,
  onOpenThread,
}: {
  announcement: Announcement;
  /** Absent for the marketing page, which has a card but no thread behind it. */
  onOpenThread?: (conversationId: string) => void;
}) {
  return (
    <article
      style={{
        background: 'var(--color-surface-raised)',
        borderLeft: '5px solid var(--color-accent-announcement)',
        borderRadius: '0 8px 8px 0',
        boxShadow: 'var(--elevation-raised)',
        padding: 'var(--space-4)',
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <span
          className="t-eyebrow inline-flex items-center gap-2"
          style={{ color: 'var(--color-accent-announcement-text)' }}
        >
          {announcement.unreadCount > 0 && <UnreadDot count={announcement.unreadCount} />}
          <Megaphone size={14} aria-hidden="true" />
          Announcement
        </span>

        {announcement.isPinned && (
          <Pin
            size={16}
            aria-label="Pinned"
            style={{ color: 'var(--color-text-muted)', flexShrink: 0 }}
          />
        )}
      </div>

      <h3
        className="t-subheading"
        style={{
          color: 'var(--color-text-primary)',
          margin: 'var(--space-2) 0 var(--space-2)',
          textWrap: 'balance',
        }}
      >
        {announcement.title}
      </h3>

      {/* Curated text (TROOP-255): an officer wrote it for the whole unit, so it may carry lists,
          emphasis and https links with their host beside them. Newlines are still kept -- a
          leader typing three lines into the box means three lines. The thread underneath is
          discussion and stays plain text. */}
      <CuratedText text={announcement.body} style={{ color: 'var(--color-text-primary)' }} />

      {/* What it hands out, below the body and in the author's order (TROOP-255). */}
      <AnnouncementAttachments attachments={announcement.attachments} />

      <p
        className="t-caption"
        style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-3) 0 0' }}
      >
        {announcement.authorDisplayName}
        {/* The role is what tells a new family this is the unit speaking rather than somebody's
            opinion. Absent when the author no longer holds a position, rather than stale. */}
        {announcement.authorRole && <> &middot; {announcement.authorRole}</>}
        {' '}&middot;{' '}
        <span className="tabular">{formatTimestamp(announcement.publishedAt)}</span>
      </p>

      {onOpenThread && announcement.conversationId && (
        <p style={{ margin: 'var(--space-3) 0 0' }}>
          <button
            type="button"
            className="link-button t-caption"
            onClick={() => onOpenThread(announcement.conversationId as string)}
          >
            <span className="inline-flex items-center gap-2">
              <MessagesSquare size={14} aria-hidden="true" />
              {announcement.unreadCount > 0
                ? `Discussion · ${announcement.unreadCount} new`
                : 'Discussion'}
            </span>
          </button>
        </p>
      )}
    </article>
  );
}
